<!-- RosePieChart.vue -->
 <!-- 玫瑰图 -->
<template>
  <div class="rose-pie-chart-container">
    <div ref="chart" :style="{ width: width, height: height }"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts';

export default {
  name: 'RosePieChart',
  props: {
    // 尺寸配置
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '400px'
    },
    
    // 数据配置
    chartData: {
      type: Array,
      default: () => [
        { value: 335, name: 'Direct' },
        { value: 310, name: 'Email' },
        { value: 274, name: 'Union Ads' },
        { value: 235, name: 'Video Ads' },
        { value: 400, name: 'Search Engine' }
      ]
    },
    
    // 样式配置
    backgroundColor: {
      type: String,
      default: '#fff'
    },
    title: {
      type: String,
      default: 'Customized Pie'
    },
    titleColor: {
      type: String,
      default: '#333'
    },
    roseType: {
      type: String,
      default: 'radius',
      validator: (value) => ['radius', 'area', ''].includes(value)
    },
    sortData: {
      type: Boolean,
      default: true
    },
    
    // 饼图半径
    radius: {
      type: [String, Number],
      default: '55%'
    },
    
    // 动画配置
    animation: {
      type: Boolean,
      default: true
    },
    animationType: {
      type: String,
      default: 'scale'
    },
    animationEasing: {
      type: String,
      default: 'elasticOut'
    },

    // 下载相关属性
    downloadFileName: {
      type: String,
      default: 'rose_pie_chart'
    },
    downloadType: {
      type: String,
      default: 'png',
      validator: (value) => ['png', 'jpeg'].includes(value)
    },
    // 是否显示工具箱
    showToolbox: {
      type: Boolean,
      default: true
    },
    // 下载时使用的背景色
    downloadBackgroundColor: {
      type: String,
      default: '#ffffff'
    },
    
    // 颜色序列配置
    colors: {
      type: Array,
      default: () => [
        '#5470c6', '#91cc75', '#fac858', '#ee6666', 
        '#73c0de', '#3ba272', '#fc8452', '#9a60b4',
        '#ea7ccc', '#7ebf42', '#e6d450', '#f2a7c3',
        '#b6a2de', '#5ab1ef', '#ffb980', '#d87a80'
      ]
    },
    
    // 新增颜色相关配置
    // 是否在数据中优先使用自定义颜色
    useDataColorPriority: {
      type: Boolean,
      default: true
    },
    // 标签颜色
    labelColor: {
      type: String,
      default: '#666'
    },
    // 标签线颜色
    labelLineColor: {
      type: String,
      default: '#666'
    },
    // 标签线长度配置
    labelLineLength: {
      type: Number,
      default: 10
    },
    labelLineLength2: {
      type: Number,
      default: 20
    },
    // 扇区边框颜色
    borderColor: {
      type: String,
      default: '#fff'
    },
    // 扇区边框宽度
    borderWidth: {
      type: Number,
      default: 1
    },
    // 扇区阴影颜色
    shadowColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.1)'
    },
    // 扇区阴影大小
    shadowBlur: {
      type: Number,
      default: 20
    },
    // 高亮阴影颜色
    emphasisShadowColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.1)'
    },
    // 高亮阴影大小
    emphasisShadowBlur: {
      type: Number,
      default: 10
    },
    // 是否使用渐变颜色
    useGradient: {
      type: Boolean,
      default: false
    },
    // 渐变起始颜色
    gradientStartColor: {
      type: String,
      default: '#ff6a00'
    },
    // 渐变结束颜色
    gradientEndColor: {
      type: String,
      default: '#ee0979'
    },
    // 圆角半径
    borderRadius: {
      type: Number,
      default: 5
    }
  },
  
  data() {
    return {
      chart: null,
      internalData: [],
      resizeTimer: null,
      internalColors: []  // 新增：内部颜色管理
    };
  },
  
  mounted() {
    this.internalData = [...this.chartData];
    this.internalColors = [...this.colors];  // 初始化内部颜色
    this.initChart();
  },
  
  beforeDestroy() {
    this.disposeChart();
  },
  
  computed: {
    // 计算总数
    total() {
      const currentData = this.internalData.length > 0 ? this.internalData : this.chartData;
      return currentData.reduce((sum, item) => sum + (item.value || 0), 0);
    }
  },
  
  methods: {
    // 准备图表数据，应用颜色
    prepareChartData(data) {
      return data.map((item, index) => {
        const dataItem = {
          value: item.value || 0,
          name: item.name || `数据${index + 1}`
        };
        
        // 确定使用哪种颜色 - 使用内部颜色
        let finalColor = null;
        
        // 优先使用数据中的颜色（如果启用优先级）
        if (this.useDataColorPriority && item.color) {
          finalColor = item.color;
        }
        // 否则使用内部颜色序列
        else if (this.internalColors && this.internalColors.length > 0) {
          finalColor = this.internalColors[index % this.internalColors.length];
        }
        
        // 如果数据中有颜色但优先级未启用，也使用数据中的颜色
        if (!finalColor && item.color) {
          finalColor = item.color;
        }
        
        // 确保有颜色
        if (!finalColor && this.internalColors && this.internalColors.length > 0) {
          finalColor = this.internalColors[index % this.internalColors.length];
        }
        
        // 如果有确定的颜色，设置itemStyle
        if (finalColor) {
          let itemStyle = {
            color: finalColor,
            borderColor: this.borderColor,
            borderWidth: this.borderWidth,
            borderRadius: this.borderRadius
          };
          
          // 如果启用渐变效果
          if (this.useGradient) {
            itemStyle.color = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: this.gradientStartColor },
              { offset: 1, color: this.gradientEndColor }
            ]);
          }
          
          // 添加阴影
          itemStyle.shadowBlur = this.shadowBlur;
          itemStyle.shadowColor = this.shadowColor;
          
          dataItem.itemStyle = itemStyle;
          
          // 设置高亮状态
          dataItem.emphasis = {
            itemStyle: {
              shadowBlur: this.emphasisShadowBlur,
              shadowOffsetX: 0,
              shadowColor: this.emphasisShadowColor,
              borderColor: this.borderColor,
              borderWidth: this.borderWidth,
              borderRadius: this.borderRadius
            }
          };
        }
        
        return dataItem;
      });
    },
    
    // 下载图片
    downloadImage() {
      if (!this.chart) {
        console.warn('图表实例未初始化');
        return false;
      }

      try {
        const dataURL = this.chart.getDataURL({
          type: this.downloadType,
          pixelRatio: 2,
          backgroundColor: this.downloadBackgroundColor,
          excludeComponents: ['toolbox']
        });

        const link = document.createElement('a');
        link.href = dataURL;
        link.download = `${this.downloadFileName}_${new Date().getTime()}.${this.downloadType}`;
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        console.log('图表下载成功');
        return true;
      } catch (error) {
        console.error('图表下载失败:', error);
        alert('下载失败，请重试');
        return false;
      }
    },

    // 获取工具箱配置
    getToolboxConfig() {
      if (!this.showToolbox) {
        return undefined;
      }
      
      return {
        feature: {
          saveAsImage: {
            title: '保存为图片',
            type: this.downloadType,
            name: this.downloadFileName,
            pixelRatio: 2,
            backgroundColor: this.downloadBackgroundColor,
            excludeComponents: ['toolbox'],
          }
        },
        right: 10,
        top: 10,
        iconStyle: {
          borderColor: '#666'
        }
      };
    },
    
    // 初始化图表
    initChart() {
      if (!this.$refs.chart) {
        console.warn('图表容器未找到');
        return;
      }
      
      try {
        this.chart = echarts.init(this.$refs.chart);
        this.updateChart();
        
        // 使用防抖的窗口 resize 事件监听
        window.addEventListener('resize', this.handleWindowResize);
      } catch (error) {
        console.error('初始化图表失败:', error);
      }
    },
    
    disposeChart() {
      if (this.chart) {
        this.chart.dispose();
        this.chart = null;
      }
      window.removeEventListener('resize', this.handleWindowResize);
      
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
    },
    
    // 防抖处理的窗口 resize 事件
    handleWindowResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 150);
    },
    
    updateChart() {
      if (!this.chart) {
        return;
      }
      
      try {
        const currentData = this.internalData.length > 0 ? this.internalData : [...this.chartData];
        
        const data = this.sortData 
          ? [...currentData].sort((a, b) => a.value - b.value)
          : currentData;
        
        // 准备带有颜色的数据
        const preparedData = this.prepareChartData(data);
        
        const option = {
          // 设置全局颜色序列 - 使用内部颜色
          color: this.internalColors,
          backgroundColor: this.backgroundColor,
          title: {
            text: this.title,
            left: 'center',
            top: 20,
            textStyle: {
              color: this.titleColor
            }
          },
          // 添加工具箱配置
          toolbox: this.getToolboxConfig(),
          tooltip: {
            trigger: 'item',
            formatter: (params) => {
              const percentage = ((params.value / this.total) * 100).toFixed(1);
              return `
                <div style="display: flex; align-items: center;">
                  <div style="width: 12px; height: 12px; background: ${params.color}; margin-right: 8px; border-radius: 2px;"></div>
                  <div>
                    <strong>${params.name}</strong><br/>
                    数值: ${params.value}<br/>
                    占比: ${percentage}%
                  </div>
                </div>
              `;
            }
          },
          visualMap: {
            show: false,
            min: this.getMinValue(),
            max: this.getMaxValue(),
            inRange: {
              colorLightness: [0, 1]
            }
          },
          series: [
            {
              name: '数据来源',
              type: 'pie',
              radius: this.radius,
              center: ['50%', '50%'],
              data: preparedData,
              roseType: this.roseType,
              label: {
                color: this.labelColor,
                formatter: (params) => {
                  const percentage = ((params.value / this.total) * 100).toFixed(1);
                  return `${params.name}: ${params.value} (${percentage}%)`;
                },
                fontSize: 12,
                fontWeight: 'normal'
              },
              labelLine: {
                lineStyle: {
                  color: this.labelLineColor,
                  width: 1
                },
                smooth: 0.2,
                length: this.labelLineLength,
                length2: this.labelLineLength2
              },
              itemStyle: {
                // 基础样式会在prepareChartData中设置，这里设置一些全局样式
                borderColor: this.borderColor,
                borderWidth: this.borderWidth,
                borderRadius: this.borderRadius,
                shadowBlur: this.shadowBlur,
                shadowColor: this.shadowColor
              },
              emphasis: {
                scale: true,
                scaleSize: 5,
                itemStyle: {
                  shadowBlur: this.emphasisShadowBlur,
                  shadowOffsetX: 0,
                  shadowColor: this.emphasisShadowColor
                },
                label: {
                  show: true,
                  fontSize: 14,
                  fontWeight: 'bold',
                  formatter: (params) => {
                    const percentage = ((params.value / this.total) * 100).toFixed(1);
                    return `${params.name}\n${params.value} (${percentage}%)`;
                  }
                }
              },
              animation: this.animation,
              animationType: this.animationType,
              animationEasing: this.animationEasing,
              animationDelay: this.animation ? () => {
                return Math.random() * 200;
              } : 0
            }
          ]
        };
        
        this.chart.setOption(option, true);
      } catch (error) {
        console.error('更新图表失败:', error);
      }
    },
    
    getMinValue() {
      const currentData = this.internalData.length > 0 ? this.internalData : this.chartData;
      if (currentData.length === 0) return 0;
      return Math.min(...currentData.map(item => item.value)) * 0.8;
    },
    
    getMaxValue() {
      const currentData = this.internalData.length > 0 ? this.internalData : this.chartData;
      if (currentData.length === 0) return 100;
      return Math.max(...currentData.map(item => item.value)) * 1.2;
    },
    
    handleResize() {
      if (this.chart) {
        try {
          this.chart.resize();
        } catch (error) {
          console.error('调整图表大小失败:', error);
        }
      }
    },
    
    // 提供给外部调用的方法
    resize() {
      this.handleResize();
    },
    
    // 更新数据
    setData(newData) {
      this.internalData = [...newData];
      this.updateChart();
    },
    
    // 显示加载动画
    showLoading() {
      if (this.chart) {
        this.chart.showLoading();
      }
    },
    
    // 隐藏加载动画
    hideLoading() {
      if (this.chart) {
        this.chart.hideLoading();
      }
    },
    
    // 清空数据
    clearData() {
      this.internalData = [];
      this.updateChart();
    },
    
    // 添加数据
    addDataItem(item) {
      this.internalData.push({ ...item });
      this.updateChart();
    },
    
    // 获取当前数据
    getCurrentData() {
      return [...this.internalData];
    },
    
    // 获取图表实例
    getInstance() {
      return this.chart;
    },
    
    // 更新颜色 - 修复：使用内部变量
    updateColors(newColors) {
      if (newColors && Array.isArray(newColors)) {
        this.internalColors = [...newColors];  // ✅ 修改内部变量
        this.$nextTick(() => {
          this.updateChart();
        });
      }
    },
    
    // 设置单个数据项颜色
    setDataItemColor(name, color) {
      const dataIndex = this.internalData.findIndex(item => item.name === name);
      if (dataIndex !== -1) {
        this.internalData[dataIndex].color = color;
        this.updateChart();
      }
    }
  },
  
  watch: {
    // 监听 colors prop 变化，同步到 internalColors
    colors: {
      handler(newColors) {
        this.internalColors = [...newColors];
        this.updateChart();
      },
      deep: true,
      immediate: true  // 立即执行一次
    },
    
    chartData: {
      handler(newData) {
        this.internalData = [...newData];
        this.updateChart();
      },
      deep: true
    },
    backgroundColor() {
      this.updateChart();
    },
    title() {
      this.updateChart();
    },
    roseType() {
      this.updateChart();
    },
    radius() {
      this.updateChart();
    },
    showToolbox() {
      this.updateChart();
    },
    // 颜色相关属性的监听
    useDataColorPriority() {
      this.updateChart();
    },
    labelColor() {
      this.updateChart();
    },
    labelLineColor() {
      this.updateChart();
    },
    borderColor() {
      this.updateChart();
    },
    borderWidth() {
      this.updateChart();
    },
    shadowColor() {
      this.updateChart();
    },
    shadowBlur() {
      this.updateChart();
    },
    emphasisShadowColor() {
      this.updateChart();
    },
    emphasisShadowBlur() {
      this.updateChart();
    },
    useGradient() {
      this.updateChart();
    },
    gradientStartColor() {
      this.updateChart();
    },
    gradientEndColor() {
      this.updateChart();
    },
    borderRadius() {
      this.updateChart();
    },
    width() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 150);
    },
    height() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 150);
    }
  },

  // 暴露方法给父组件
  expose: [
    'downloadImage', 
    'resize', 
    'setData', 
    'clearData', 
    'addDataItem', 
    'getCurrentData', 
    'getInstance',
    'updateColors',
    'setDataItemColor'
  ]
};
</script>

<style scoped>
.rose-pie-chart-container {
  width: 100%;
  height: 100%;
  position: relative;
}
</style>